import useSettings from "../settings/useSettings.js";
import RenaiEventAttendance from "../hacks/RenaiEventAttendance.js";
import useAuth from "../auth/useAuth.js";

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from '@material-ui/core/styles';
import {
    Card, Button, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Tooltip, Box, Tabs, TabPanel, Tab, Divider, Typography,
    Accordion, AccordionSummary, AccordionDetails
, } from '@material-ui/core';
import { Edit, Link as LinkIcon, CheckCircle, ArrowBack, ExpandMore } from '@material-ui/icons';
import Moment from 'react-moment';

import { ViewState } from '@devexpress/dx-react-scheduler';
import {
    Scheduler,
    DayView,
    WeekView,
    MonthView,
    Appointments,
    Toolbar,
    ViewSwitcher,
    DateNavigator, AppointmentTooltip,
    TodayButton, Resources
} from '@devexpress/dx-react-scheduler-material-ui';

const schedulerResources = [{
    fieldName: 'eventColorGroup',
    title: 'Status',
    instances: [
        { id: 'uow', text: 'Renaissance Event', color: 'var(--darkBlue)' }
    ],
}];

const calendarStyles = makeStyles((theme) => ({
    appointment: {
        "&:hover": {
            opacity: 0.7
        }
    }
}));

const mapPagesToScheduler = (pages, newAttendanceIds) => {
    const output = [];
    pages.forEach(p => {
        if (!p.eventDates)
            return;
        p.eventDates.forEach((e,i) => {
            output.push({
                id: p.id+" "+i,
                title: p.title,
                startDate: e.eventStartDate,
                endDate: e.eventEndDate,
                eventColorGroup: 'uow',
                hasAttended: newAttendanceIds.indexOf(p.id) !== -1 ? true : (p.extensionData.AttendanceData ?? false),
                data: p
            });
        });
    });
    return output;
};

function CustomAppointment({ children, data, ...restProps }) {
    const classes = calendarStyles();
    return (
        <Appointments.Appointment data={data} {...restProps} className={classes.appointment}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: '6px' }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {children}
                </div>
                {data.hasAttended && (
                    <CheckCircle style={{ color: '#4caf50', fontSize: '14px', marginLeft: '4px', flexShrink: 0 }} />
                )}
            </div>
        </Appointments.Appointment>
    );
}

function CustomTooltipContent({ appointmentData, onSubmitAttendance }) {
    const page = appointmentData.data;
    const auth = useAuth();

    const [canContribute, setCanContribute] = useState(false);
    const [canApprove, setCanApprove] = useState(false);

    useEffect(() => {
        if (page) {
            setCanContribute(auth.user.id && auth.user?.roles && auth.user.roles.indexOf("renaissance_events.contributor") !== -1);
            setCanApprove(auth.user.id && auth.user?.roles && auth.user.roles.indexOf("renaissance_events.approver") !== -1);
        }
    }, [auth, page]);

    if (!appointmentData) {
        return null;
    }
    const hasStarted = new Date(appointmentData.startDate) <= new Date();

    return (
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '280px' }}>
            <Box style={{ display: 'flex' }}>
                <Typography variant="h4" component={RouterLink}
                    to={`/${page?.area}/${page?.url || page?.family}`} style={{ flex: 1, margin: '0 0 4px 0', fontSize: '16px', padding: 0 }}>
                    {appointmentData.title}
                </Typography>
                {canContribute && (
                    <MiniButton help="Make changes to the page" icon={<Edit style={{ fontSize: 18 }} />} link={`/${page.area}/${page.id}/edit`} />
                )}
            </Box>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {page?.state === "workflow" && canApprove && (
                    <Chip label="Workflow" size="small" variant="outlined" color="primary" />
                )}
                {page?.redirect && canApprove && (
                    <Chip label="Redirect" size="small" variant="outlined" color="secondary" />
                )}
                {appointmentData.hasAttended && (
                    <Chip icon={<CheckCircle style={{ color: 'green' }} />} label="Attended By You" size="small" />
                )}
            </div>

            {page && (
                <div style={{ marginTop: '4px', paddingTop: '8px' }}>
                    <Typography variant="body1" dangerouslySetInnerHTML={{ __html: page.description }}></Typography>
                    <RenaiEventAttendance page={page} mini onSubmitAttendance={onSubmitAttendance} />
                </div>
            )}
        </div>
    );
}

function MiniButton({ help, icon, link }) {
    return <Tooltip title={help}>
        <Button size="small" style={{ padding: '4px', margin: 0,  minWidth: 'auto' }} color="primary" component={RouterLink} to={link}>{icon}</Button>
    </Tooltip>;
}

function ClickableTimeTableCell({ onCellClick, ...restProps }) {
    const { startDate } = restProps;
    return <MonthView.TimeTableCell {...restProps} onClick={() => onCellClick?.(startDate)} style={{ cursor: 'pointer' }} />;
}

function RenaiEventPagePreviewGrid({ pages, onSubmitAttendance, toggleShowSorter, newAttendanceIds, tags }) {
    const classes = calendarStyles();
    const [mode, setMode] = useState("calendar");

    const [currentDate, setCurrentDate] = useState(new Date());
    const [calendarView, setCalendarView] = useState("Month");

    const [tooltipVisible, setTooltipVisible] = useState(false);

    const schedulerData = useMemo(() => mapPagesToScheduler(pages, newAttendanceIds), [pages, newAttendanceIds]);

    const handleAttendanceSubmit = async (eventPayload) => {
        if (onSubmitAttendance) {
            await onSubmitAttendance(eventPayload);
        }
        setTooltipVisible(false);
    };

    const handleChange = (e, v) => {
        setMode(v);
    };

    const TimeTableCellWrapper = useMemo(() => {
        return (props) => (
            <ClickableTimeTableCell
                {...props}
                onCellClick={(clickedDate) => {
                    setCurrentDate(clickedDate);
                    setCalendarView("Day");
                }}
            />
        );
    }, []);

    const datelessPages = useMemo(() => pages.filter(p => !p.eventDates || p.eventDates.length == 0), [pages]);

    const CustomToolbarFlexibleSpace = useMemo(() => {
        return (props) => (
            <Toolbar.FlexibleSpace {...props}>
                <div style={{ marginLeft: 'auto', paddingRight: '16px' }}>
                    {calendarView === "Day" && (
                        <Button variant="outlined" color="primary" size="small"
                            startIcon={<ArrowBack />} onClick={() => setCalendarView("Month")} >
                            Month View</Button>
                    )}
                </div>
            </Toolbar.FlexibleSpace>
        );
    }, [calendarView]);

    useEffect(() => {
        toggleShowSorter(false);
    }, [mode]);

    const TooltipContentWrapper = useMemo(() => {
        return (props) => <CustomTooltipContent {...props} onSubmitAttendance={handleAttendanceSubmit} />;
    }, [onSubmitAttendance]);

    return (
        <>
            <Tabs id="RenaiEventPagePreviewGrid" value={mode}
                onChange={handleChange} aria-label="event view" style={{ marginBottom: '16px' }}>
                <Tab label="Scheduled" value="calendar" />
                <Tab label="Any-time" value="anytime" />
                <Tab label="List" value="list" />
            </Tabs>

            {mode === "calendar" && <Scheduler data={schedulerData} className={classes.calendar}>
                <ViewState currentDate={currentDate} onCurrentDateChange={setCurrentDate} currentViewName={calendarView} onCurrentViewNameChange={setCalendarView} />

                <MonthView timeTableCellComponent={TimeTableCellWrapper} />
                <DayView cellDuration={60} startDayHour={8} endDayHour={19} />
                <Appointments appointmentComponent={CustomAppointment} />

                <AppointmentTooltip contentComponent={TooltipContentWrapper} visible={tooltipVisible} onVisibilityChange={setTooltipVisible} />
                <Resources data={schedulerResources} mainResourceName="eventColorGroup" />
                <Toolbar flexibleSpaceComponent={CustomToolbarFlexibleSpace} />
                <DateNavigator />
            </Scheduler>}
            {mode === "anytime" && <GenericPagePreviews pages={datelessPages} onSubmitAttendance={onSubmitAttendance} tags={tags} />}
            {mode === "list" && <GenericPagePreviews pages={pages} onSubmitAttendance={onSubmitAttendance} tags={tags} />}
        </>
    );
}

const genericPagesStyles = makeStyles((theme) => ({
    accordion: {
        "&::before": {
            background: 'none'
        }
    }
}));
function GenericPagePreviews({ pages, onSubmitAttendance, tags }) {
    const auth = useAuth();
    const settings = useSettings();
    const styles = genericPagesStyles();
    const [canContribute, setCanContribute] = useState(false);
    useEffect(() => {
        if (auth?.user?.roles) {
            const roles = auth.user.roles;
            setCanContribute(roles.includes("renaissance_events.contributor"));
        }
    }, [auth]);

    const primaryTags = useMemo(() => {
        const primaryCategoryId = settings.get("Renai diagram tag category");
        const primaryCategory = tags.find(t => t.id == primaryCategoryId);
        if (!primaryCategory)
            return [];
        return primaryCategory.options;
    }, [tags, settings]);

    const categories = useMemo(() => {
        const cats = [];
        for (let t of primaryTags) {
            const cat = {
                id: t.id,
                title: t.name,
                pages: []
            };

            for (let p of pages) {
                if (p.options.includes(t.id)) {
                    cat.pages.push({ ...p });
                }
            }

            cats.push(cat);
        }

        return cats;
    }, [primaryTags, pages]);

    return categories.map((c, i) => c.pages.length > 0 ? <Accordion elevation={0} key={c.id} className={styles.accordion}>
        <AccordionSummary expandIcon={<ExpandMore />} aria-controls={`panel${i}-content`} id={`panel${i}-header`}>
            <Typography variant="h3">{c.title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
            {c.pages.map(p => (
                <PagePreviewRenaiEvent
                    canContribute={canContribute}
                    key={p.id}
                    page={p}
                    onSubmitAttendance={onSubmitAttendance}
                />
            ))}
        </AccordionDetails>
    </Accordion> : null);
    return 
}

const previewStyles = makeStyles((theme) => ({
    card: {
        listStyle: 'none',
        backgroundColor: 'var(--white)',
        border: '1px solid var(--lightGrey)',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0,22,65,0.05)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    },
    heading: { padding: '0 !important', margin: '0 !important' },
}));

function PagePreviewRenaiEvent({ page, onSubmitAttendance, canContribute }) {
    const styles = previewStyles();
    return <Card component="li" className={styles.card}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: '8px' }}>
                <RouterLink to={"/" + page.area + "/" + (page.url || page.family)}>
                    <Typography variant="h6" component='h3' className={styles.heading}>{page?.title || ""}</Typography>
                </RouterLink>
                {canContribute && <MiniButton help="Make changes to the page" icon={<Edit />} link={"/" + page.area + "/" + page.id + "/edit"} />}
                {page?.state === "workflow" && <Chip label="Workflow" variant="outlined" size="small" />}
                {page?.redirect && <Chip label="Redir" size="small" />}
            </Box>

            <Box display="flex" alignItems="center" style={{ gap: '4px' }}>
                <RenaiEventAttendance page={page} mini onSubmitAttendance={onSubmitAttendance} />
            </Box>
        </Box>
        
        {page.eventDates && page.eventDates.length > 0 && <Box display="flex" style={{ gap: "0.5rem", flexWrap: 'wrap' }}>{page.eventDates.map((e,i) => <Chip key={i} label={<>
            <Moment format="hh:mma DD/MM/YY">{e.eventStartDate}</Moment>&nbsp;-&nbsp;<Moment format="hh:mma DD/MM/YY">{e.eventEndDate}</Moment></>} variant="outlined" size="small" />)}</Box>}
        {page?.description && <Box mb={2}>
            <Typography
                variant="body2"
                style={{ color: 'var(--black)', opacity: 0.8, lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: page.description }}
            />
        </Box>}
    </Card>;
}

export { PagePreviewRenaiEvent, RenaiEventPagePreviewGrid };
